import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  type BillingPrice,
  GRANT_FOREVER,
  MEMBER_PLAN_LABELS,
  memberPlan,
  subscriptionPaid,
  subscriptionStanding,
  VENDOR_MARK_DATE,
  vendorMarkLiveAt,
} from '@floorpoll/shared';
import { trackEvent } from '../../analytics/events.js';
import { FormError, FormNotice } from '../../components/Form.js';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { type BillingView, getBilling, openPortal, startCheckout } from '../../lib/billingApi.js';

/**
 * The plan, on the account page.
 *
 * Split in two on purpose: `PlanCard` owns the fetching and the redirects,
 * `PlanView` is pure. The console needs a session and this needs Stripe, so
 * the pure half is what /dev/gallery renders to show every plan state at once
 * without either.
 *
 * Two things this component is careful about, both of them honesty:
 *
 * A plan belongs to a **workspace**, not to a person, and a person can be in
 * several. So the workspace is named beside the plan — otherwise someone with
 * a personal and a shared workspace cannot tell which one they just paid for.
 *
 * Entitlement is written by Stripe's webhook, not by the redirect back from
 * Checkout, so the two can land a second apart. Rather than show "Free" to
 * someone who has just paid, a return from a successful checkout re-checks a
 * few times and lets the card flip on its own.
 *
 * State is drawn the way the builder's cue rail draws readiness — fill, not
 * hue. Tungsten stays reserved for liveness; a subscription is a state, not a
 * broadcast, so it never earns the lamp.
 */

interface PlanOption {
  price: BillingPrice;
  name: string;
  amount: string;
  period: string;
  note: string;
}

/**
 * Display copy for the catalogue. The API deliberately returns entitlement
 * rather than prices, so these strings mirror the Stripe catalogue by hand —
 * change them together (see docs/BILLING.md).
 */
const SUBSCRIPTIONS: PlanOption[] = [
  {
    price: 'marquee_annual',
    name: 'Marquee, yearly',
    amount: '$144',
    period: 'a year',
    note: '$12 a month · saves $36',
  },
  {
    price: 'marquee_monthly',
    name: 'Marquee, monthly',
    amount: '$15',
    period: 'a month',
    note: 'Month to month',
  },
];

const EVENT_PASS: PlanOption = {
  price: 'event_pass',
  name: 'Event Pass',
  amount: '$39',
  period: 'once',
  note: '30 days of Marquee, no subscription',
};

/** A cell where a plan simply does not carry the capability. */
const NOT_INCLUDED = '—';

/**
 * What actually differs between the plans.
 *
 * Rows that would read the same on both are deliberately absent — anonymous
 * voting and nothing to install never change with the plan, and saying that
 * once above the table beats repeating a tick down two columns.
 *
 * The audience row leads, because it is the row buyers compare on and the one
 * where the answer is unusual: the free plan holds 100 people per
 * presentation, and paid plans stop counting altogether rather than moving to
 * a bigger number. Every competitor tiers the audience all the way up.
 *
 * Kept in step with docs/GTM.md, where the split was decided.
 */
const PLAN_COMPARISON: { capability: string; house: string; marquee: string }[] = [
  { capability: 'People per poll', house: 'Up to 100', marquee: 'No limit' },
  { capability: 'Active polls', house: 'Three', marquee: 'Unlimited' },
  {
    capability: 'Themes',
    house: 'The standard set',
    marquee: 'Your colours and logo, on the screen and every phone',
  },
  {
    capability: 'Follow-up after a vote',
    house: NOT_INCLUDED,
    marquee: 'A message or link per option',
  },
  { capability: 'Results', house: 'The run you are in', marquee: 'Every run, exportable as CSV' },
  { capability: 'The join screen', house: 'Powered by FloorPoll', marquee: 'Your name instead' },
];

/** The free-plan headline: what the plan does not change. */
const ON_EVERY_PLAN =
  'Anonymous voting and nothing for your audience to install — on every plan, always. The free plan holds 100 people per presentation; paid plans stop counting.';

/**
 * Which rows of the table above the API actually enforces. The rest describe a
 * product early access does not yet withhold anything for, and saying which is
 * which is not a disclaimer — it is the truth about this account. A table
 * implying three polls were the cap would describe a product that does not
 * exist.
 *
 * Two rows now, not one: the join-screen row becomes true when the FloorPoll
 * mark reaches public surfaces (#91). Until that date it is still a promise,
 * and this is where a free workspace is told it is coming — the notice of
 * record is §8 of the Terms, which every account is asked to accept.
 *
 * Narrow this line as each further limit lands, and delete it with the last.
 */
function earlyAccessNote(at: Date): string {
  const enforced = vendorMarkLiveAt(at)
    ? 'The audience limit and the FloorPoll mark on your public surfaces are live.'
    : // VENDOR_MARK_DATE, not longDate(VENDOR_MARK_FROM). The switch-on is
      // midnight UTC, so formatting it in the reader's timezone prints
      // "September 30" to most of the Americas while §8 of the Terms prints
      // 1 October — one product naming two days for one event. The constant
      // pair is the day, and `billing.test.ts` keeps its two spellings in step.
      `The audience limit is live, and free polls start carrying a FloorPoll mark on ${VENDOR_MARK_DATE}.`;
  return `${enforced} Everything else in this table is not enforced yet — you have all of it while FloorPoll is in early access.`;
}

/** How long to keep re-checking after a paid checkout before giving up. */
const CONFIRM_ATTEMPTS = 5;
const CONFIRM_INTERVAL_MS = 2000;

function longDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

function PlanShell({ children }: { children: React.ReactNode }) {
  return (
    <section className="panel plan">
      <h2>Plan</h2>
      {children}
    </section>
  );
}

export function PlanCard({
  workspaceId,
  workspaceName,
  manageable,
}: {
  workspaceId: string;
  workspaceName: string;
  /** Mirrors `workspace:manage`; every billing route requires it server-side. */
  manageable: boolean;
}) {
  // undefined while loading, null when billing is unavailable in this
  // environment — the two must not render the same thing.
  const [view, setView] = useState<BillingView | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<BillingPrice | 'portal' | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const returned = searchParams.get('billing');
  const justPaid = returned === 'success';
  const { refreshMe } = useAuth();

  useEffect(() => {
    if (!manageable) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function load(attempt: number) {
      try {
        const result = await getBilling(workspaceId);
        if (cancelled) return;
        setView(result);
        if (justPaid && result.pro) {
          // The workspace switcher names the plan from the summary `/me`
          // returns, which was fetched before this purchase existed. Nothing
          // else re-reads it, so without this the header goes on saying Free
          // until the next full load.
          void refreshMe();
        }
        if (justPaid && !result.pro && attempt < CONFIRM_ATTEMPTS) {
          timer = setTimeout(() => void load(attempt + 1), CONFIRM_INTERVAL_MS);
        }
      } catch {
        // Offline, or a stage without billing. The rest of the account page
        // is still useful, so this section steps aside rather than erroring.
        if (!cancelled) setView(null);
      }
    }

    void load(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // refreshMe is safe to depend on only because auth.tsx gives it a stable
    // identity (useCallback with no deps). If that ever stopped being true,
    // this would loop rather than just re-render: refreshMe sets state on
    // AuthProvider, which would hand this effect a new refreshMe right after
    // the effect had just called it.
  }, [workspaceId, manageable, justPaid, refreshMe]);

  async function go(action: () => Promise<{ url: string }>, key: BillingPrice | 'portal') {
    setError(null);
    setBusy(key);
    // Before the Checkout session exists, which is the only reason it is worth
    // sending: Stripe records every session it created, so the click that never
    // became one — billing unreachable, or a change of mind at the card form —
    // is the half neither system can see. Managing an existing plan is not an
    // intent to buy and is not counted.
    if (key !== 'portal') trackEvent({ name: 'upgrade_clicked', price: key });
    setSearchParams({}, { replace: true });
    try {
      const { url } = await action();
      window.location.assign(url);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not reach billing. Try again.');
      setBusy(null);
    }
  }

  if (!manageable) {
    return (
      <PlanShell>
        <div className="plan-state">
          <span className="plan-mark plan-mark-off" aria-hidden="true" />
          <div>
            <p className="plan-headline">Managed by an admin</p>
            <p className="plan-detail">
              Only an admin of {workspaceName} can see or change its plan.
            </p>
          </div>
        </div>
      </PlanShell>
    );
  }

  if (view === undefined) {
    return (
      <PlanShell>
        <p className="muted">Checking…</p>
      </PlanShell>
    );
  }

  // Billing is switched off in this environment: no plan, nothing to say.
  if (view === null || !view.configured) return null;

  return (
    <PlanView
      view={view}
      workspaceName={workspaceName}
      busy={busy}
      error={error}
      notice={
        justPaid
          ? view.pro
            ? 'Payment received. Everything below is unlocked.'
            : 'Payment received — confirming it with Stripe now.'
          : returned === 'cancelled'
            ? 'Checkout cancelled. Nothing was charged.'
            : null
      }
      onBuy={(price) => void go(() => startCheckout(workspaceId, price), price)}
      onManage={() => void go(() => openPortal(workspaceId), 'portal')}
    />
  );
}

export interface PlanViewProps {
  view: BillingView;
  workspaceName: string;
  /**
   * The clock the early-access note reads, injectable so this half stays pure.
   * `PlanCard` passes the real one; /dev/gallery and the tests pass a fixed
   * date, because a card that renders a different sentence depending on the
   * day is a card nothing can pin.
   */
  at?: Date;
  busy?: BillingPrice | 'portal' | null;
  error?: string | null;
  notice?: string | null;
  onBuy?: (price: BillingPrice) => void;
  onManage?: () => void;
}

/** The card itself, given everything it needs. No fetching, no navigation. */
export function PlanView({
  view,
  workspaceName,
  at = new Date(),
  busy = null,
  error = null,
  notice = null,
  onBuy,
  onManage,
}: PlanViewProps) {
  const billing = view.billing;
  const status = billing?.subscriptionStatus;
  /** Any subscription on record, however it ended — the portal still opens. */
  const hasSubscriptionRecord = status !== undefined;
  /**
   * A subscription that has not ended: it renews, or it is mid-dunning.
   *
   * The rule comes from shared rather than being restated here — it is the same
   * question the operator table and the webhook ask, and the three answers
   * drifted once already.
   */
  const hasStandingSubscription = subscriptionStanding(status);
  const pastDue = status === 'past_due';
  /**
   * A subscription that has actually been charged for the period it is in —
   * the other half of `hasStandingSubscription`, and the one that licenses a
   * promise of a renewal date. Pulled from shared for the same reason as its
   * counterpart: a local `status === 'active' || status === 'trialing'` here
   * would have been a fourth restatement of a rule already written twice.
   */
  const hasPaidSubscription = subscriptionPaid(status);

  // `view.pro` is the server's authoritative answer and leads here. Deriving
  // the display from status alone showed a cancelled-but-unexpired
  // subscription as "Free" and offered to sell it again, while the workspace
  // was in fact still Marquee.
  const onSubscription = view.pro && billing?.source === 'subscription';
  const onPass = view.pro && billing?.source === 'event_pass';
  /**
   * Pro because an operator said so. Presented exactly as a paid plan: no
   * badge, no marker, and above all no offer to buy what they already have.
   *
   * The sentinel counts as a grant even when `source` does not say so, because
   * `grantPlan` is the only writer that ever produces `GRANT_FOREVER` — so a
   * record carrying it is a comp whose `source` was lost, not an unknown. Four
   * things go wrong at once without this: the date branch below prints
   * "December 31, 9999", `mark` draws borrowed time on a plan that does not
   * lapse, and the offer tries to sell Marquee to a workspace that has it.
   */
  const onGrant = view.pro && (billing?.source === 'grant' || billing?.proUntil === GRANT_FOREVER);

  // Half-set is borrowed time: an Event Pass, a subscription that will not
  // renew, and one whose payment failed and is running on grace. A grant is
  // none of those — nothing about it is going to lapse.
  const mark = !view.pro
    ? 'off'
    : onGrant
      ? 'on'
      : onPass || pastDue || billing?.cancelAtPeriodEnd || !hasStandingSubscription
        ? 'temporary'
        : 'on';
  // From shared, so this card and the workspace switcher cannot name the same
  // plan two different things. It also reads an entitled record with no
  // `source` as Marquee rather than Free, which is what `mark` above has
  // always drawn it as.
  const headline = MEMBER_PLAN_LABELS[memberPlan(view.pro, billing?.source)];

  // Dates people are told come from renewsAt, never proUntil: proUntil carries
  // the dunning grace on top, so it reads days later than the real one. Under a
  // grant proUntil is the far-future sentinel, so it is excluded outright
  // rather than merely deprioritised.
  const endsOn = onGrant ? billing?.renewsAt : (billing?.renewsAt ?? billing?.proUntil);

  let detail: string;
  if (onGrant) {
    // Under a comp, nothing about the subscription decides when Marquee ends —
    // it does not end. So every grant case leads with the plan being on, and
    // only then says something true about the subscription underneath it.
    //
    // The subscription's own state is reported, never the *consequence* of it:
    // "update your card to keep Marquee" and "Marquee until <date>" are both
    // false when a grant is what is holding the plan up. Deciding `onGrant`
    // before anything else is what keeps this branch from contradicting `mark`,
    // which already checks it first.
    const aside = pastDue
      ? ' That last subscription payment did not go through, though it is not what is keeping Marquee on.'
      : // Guarded by hasPaidSubscription too, not just cancelAtPeriodEnd: only a
        // subscription that has actually been charged for its current period
        // may be described in the present tense as running to a date. unpaid,
        // canceled and paused all keep cancelAtPeriodEnd set from before they
        // ended, and the webhook has already clamped subscriptionUntil down
        // for each of them — so without this guard the sentence would assert a
        // subscription this system has already voided is still running.
        hasPaidSubscription && billing?.cancelAtPeriodEnd && endsOn
        ? ` Your subscription runs to ${longDate(endsOn)} and will not renew.`
        : // Only a subscription that has actually paid gets a renewal date. An
          // `incomplete` one never cleared authentication, so promising it a
          // renewal is the same false promise the free-plan case already forbids.
          hasPaidSubscription && endsOn
          ? ` Your subscription renews ${longDate(endsOn)}.`
          : '';
    detail = `Everything in Marquee is on.${aside}`;
  } else if (pastDue) {
    detail = 'That last payment did not go through. Update your card to keep Marquee.';
  } else if (onSubscription && endsOn) {
    detail =
      billing?.cancelAtPeriodEnd || !hasStandingSubscription
        ? `Marquee until ${longDate(endsOn)}. It will not renew.`
        : `Renews ${longDate(endsOn)}.`;
  } else if (view.pro && billing?.proUntil) {
    // An Event Pass and a record naming no component read the same to a
    // member: entitled right now, running out on this date, with no
    // subscription behind it — which is why the offer below still sells one
    // rather than treating either as already covered.
    //
    // `view.pro` is doing real work here, not just `billing?.proUntil`: a
    // lapsed record still carries a `proUntil` in the past once entitlement
    // has run out, and without this guard that date would be read as a
    // promise sitting under a headline that already says Free.
    //
    // `proUntil`, not `endsOn`, for the reason the pass already used it:
    // `endsOn` prefers `renewsAt`, which a workspace that once subscribed can
    // still carry long after that subscription died.
    detail = `Marquee until ${longDate(billing.proUntil)}.`;
  } else {
    detail = ON_EVERY_PLAN;
  }

  return (
    <PlanShell>
      <FormNotice message={notice} />

      <div className="plan-state">
        <span className={`plan-mark plan-mark-${mark}`} aria-hidden="true" />
        <div>
          <p className="plan-headline">
            {headline}
            <span className="plan-scope"> · {workspaceName}</span>
          </p>
          <p className="plan-detail">{detail}</p>
        </div>
        {/* Any subscription on record, even a cancelled one: the portal is
            also where invoices and receipts live. */}
        {hasSubscriptionRecord ? (
          <button type="button" className="secondary" disabled={busy !== null} onClick={onManage}>
            {busy === 'portal' ? 'Opening…' : 'Manage plan'}
          </button>
        ) : null}
      </div>

      <FormError message={error} />

      {/* Subscriptions are sold to a workspace that has no subscription to
          show for itself — which includes one running on an Event Pass, where
          a subscription is the thing that gives continuity, and excludes one
          still running out a cancelled subscription, where buying again would
          open a second one alongside it. */}
      {hasStandingSubscription || onSubscription || onGrant ? null : (
        <div className="plan-offer">
          {/* What the money actually buys, stated as a difference rather than
              a feature list: the House column is what this workspace has
              today, so the Marquee column reads as the gain. */}
          <div className="plan-compare-wrap">
            <table className="plan-compare">
              <thead>
                <tr>
                  <th scope="col">
                    <span className="visually-hidden">Capability</span>
                  </th>
                  <th scope="col">House · free</th>
                  <th scope="col">Marquee</th>
                </tr>
              </thead>
              <tbody>
                {PLAN_COMPARISON.map((row) => (
                  <tr key={row.capability}>
                    <th scope="row">{row.capability}</th>
                    <td>{row.house}</td>
                    <td className="plan-compare-gain">{row.marquee}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="plan-early-access">{earlyAccessNote(at)}</p>

          <div className="plan-options">
            {SUBSCRIPTIONS.map((option, index) => (
              <button
                key={option.price}
                type="button"
                // The yearly option leads, drawn with a stronger edge rather
                // than a badge in a colour this system does not have.
                className={`plan-option${index === 0 ? ' plan-option-lead' : ''}`}
                disabled={busy !== null}
                onClick={() => onBuy?.(option.price)}
              >
                <span className="plan-option-name">{option.name}</span>
                <span className="plan-option-price">
                  {option.amount}
                  <span className="plan-option-period"> {option.period}</span>
                </span>
                <span className="plan-option-note">
                  {busy === option.price ? 'Opening…' : option.note}
                </span>
              </button>
            ))}
          </div>

          {/* A one-time purchase is a different kind of thing from a
              subscription, so it sits below the rule rather than posing as a
              third plan.

              Withheld from someone already running on a pass: a pass runs 30
              days from the day it is bought and the webhook takes the later of
              the two dates rather than adding them, so a second one bought
              mid-run would buy far less than its price implies. Subscriptions
              stay on offer, which is what actually gives them continuity. */}
          {onPass ? null : (
            <button
              type="button"
              className="plan-oneoff"
              disabled={busy !== null}
              onClick={() => onBuy?.(EVENT_PASS.price)}
            >
              <span className="plan-oneoff-text">
                <strong>{EVENT_PASS.name}</strong>
                <span className="muted"> — {EVENT_PASS.note}</span>
              </span>
              <span className="plan-oneoff-price">
                {busy === EVENT_PASS.price
                  ? 'Opening…'
                  : `${EVENT_PASS.amount} ${EVENT_PASS.period}`}
              </span>
            </button>
          )}
        </div>
      )}
    </PlanShell>
  );
}
